import { BenchmarkComparisonSeries, DateStr } from '@ndvr/reporting-api';

import { ExternalSeriesOverlay, toChartValue } from './components/NavChart';
import { NAV_CHART_BENCHMARK_LINE_COLORS } from './constants';

// Shown in the chart footnote while any benchmark line is drawn.
export const BENCHMARK_DISCLOSURE =
    'Benchmark comparisons simulate investing this account’s cash flows in the benchmark; ' +
    'values are hypothetical and do not reflect fees or trading costs.';

// Overlay keys are namespaced so a benchmark name can never collide with a
// PerformanceData field overlay (e.g. netInvestedCapital).
const BENCHMARK_OVERLAY_KEY_PREFIX = 'benchmark:';

export const benchmarkOverlayKey = (benchmarkName: string) => BENCHMARK_OVERLAY_KEY_PREFIX + benchmarkName;

// A benchmark's color by its position in the available list, so it keeps its color in the chart,
// Trailing returns and the risk scatter no matter which subset is selected.
export const benchmarkColor = (benchmarkName: string, benchmarkNames: string[]) => {
    const position = Math.max(benchmarkNames.indexOf(benchmarkName), 0);
    return NAV_CHART_BENCHMARK_LINE_COLORS[position % NAV_CHART_BENCHMARK_LINE_COLORS.length];
};

// reportDate → chart value per benchmark, using the shared NaN-for-missing convention
// so calendar gaps render as gaps rather than false zeros.
export const toBenchmarkValueMaps = (series: BenchmarkComparisonSeries[]): Map<string, ReadonlyMap<DateStr, number>> =>
    new Map(
        series.map((benchmark) => [
            benchmark.benchmarkName,
            new Map(benchmark.points.map((point) => [point.reportDate, toChartValue(point.value)])),
        ]),
    );

const NO_VALUES: ReadonlyMap<DateStr, number> = new Map();

type BuildBenchmarkOverlaysParams = {
    // The benchmarks picked in the header.
    benchmarkNames: string[];
    // Every available benchmark; fixes each one's color.
    availableBenchmarks: string[];
    valuesByBenchmark: Map<string, ReadonlyMap<DateStr, number>>;
    // Whether the comparison query is currently fetching.
    isFetching?: boolean;
};

// One dashed overlay per picked benchmark, colored by its position among the available ones so it
// keeps its color no matter which subset is picked. Values stay empty until the comparison query has
// data for the benchmark.
//
// A benchmark is pending while the query fetches and the (possibly kept-previous) data has no series
// for it — i.e. just after it was picked. Benchmarks already on screen keep rendering without a
// spinner while a new date range or account selection refetches them.
export const buildBenchmarkOverlays = ({
    benchmarkNames,
    availableBenchmarks,
    valuesByBenchmark,
    isFetching = false,
}: BuildBenchmarkOverlaysParams): ExternalSeriesOverlay[] =>
    benchmarkNames.map((name) => ({
        key: benchmarkOverlayKey(name),
        source: 'external',
        label: name,
        color: benchmarkColor(name, availableBenchmarks),
        dashed: true,
        disclosure: BENCHMARK_DISCLOSURE,
        isPending: isFetching && !valuesByBenchmark.has(name),
        values: valuesByBenchmark.get(name) ?? NO_VALUES,
    }));
